/**
 * Tonal palettes for the Carbon theme.
 *
 * A palette is built from a single base colour, which becomes step 500: the lighter steps mix it
 * with white, the darker steps with black. The ratios are fixed, so every palette — the Carbon
 * blue and each client colour — shares the same rhythm. Step 600 (the hover colour) is the base
 * darkened by 15 %, which for the Carbon blue #2e6bf3 lands exactly on the brand hover #275bcf.
 */
export type PaletteStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
export type TonalPalette = Record<PaletteStep, string>;

const TINTS: Partial<Record<PaletteStep, number>> = { 50: 0.94, 100: 0.86, 200: 0.72, 300: 0.52, 400: 0.28 };
const SHADES: Partial<Record<PaletteStep, number>> = { 600: 0.15, 700: 0.3, 800: 0.45, 900: 0.58, 950: 0.72 };

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function toRgb(hex: string): [number, number, number] {
    return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(rgb: number[]): string {
    return `#${rgb.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`;
}

/** Mixes `weight` (0–1) of `target` into `base`, channel by channel in sRGB. */
function mix(base: string, target: string, weight: number): string {
    const from = toRgb(base);
    const to = toRgb(target);
    return toHex(from.map((channel, i) => channel * (1 - weight) + to[i] * weight));
}

export function tonalPalette(base: string): TonalPalette {
    if (!HEX_COLOR.test(base)) {
        throw new Error(`tonalPalette expects a #rrggbb colour, got "${base}"`);
    }
    const color = base.toLowerCase();
    const palette = { 500: color } as TonalPalette;
    for (const [step, weight] of Object.entries(TINTS)) {
        palette[Number(step) as PaletteStep] = mix(color, '#ffffff', weight);
    }
    for (const [step, weight] of Object.entries(SHADES)) {
        palette[Number(step) as PaletteStep] = mix(color, '#000000', weight);
    }
    return palette;
}
